//node_modules
import { NextPage } from "next";
import Link from "next/link";
import React, { useState } from "react";
import { Row, Col, Card, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUpdateEffect } from "react-use";
import { faPhone, faEnvelope, faMessage, faEnvelopeCircleCheck } from "@fortawesome/free-solid-svg-icons";

//libs
import { css } from "@adjust/libs/emotion";

//alerts
import AlertComponent, { AlertProvider } from "@adjust/website/src/components/AlertComponent";

//layout
import SiteLayout from "src/layouts/SiteLayout";

//components
import PageHeaderComponent from "src/components/PageHeaderComponent";

//locals
import FormComponent from "./components/FormComponent";
import MetaComponent from "./components/MetadataComponent";

// -------------------------------------
// Styles
// -------------------------------------

const pageBackground = css({
  background: "#f7f9fc",
});

const hero = css({
  maxWidth: 900,
  margin: "0 auto",
});

const card = css({
  border: "none",
  borderRadius: "1rem",
  boxShadow: "0 6px 18px rgba(0,0,0,.06)",
  transition: "all .2s ease",

  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 28px rgba(0,0,0,.08)",
  },
});

const iconCircle = css({
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: "linear-gradient(135deg,#2d6cdf,#1f4fa8)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#fff",
  margin: "0 auto 1rem",
});

const contactLink = css({
  textDecoration: "none",
  color: "inherit",

  "&:hover": {
    color: "#0d6efd",
  },
});

const muted = css({
  color: "#6c757d",
});

const successContainer = css({
  minHeight: "70vh",
  display: "flex",
  alignItems: "center",
});

const blackLinkCss = css({
  color: "inherit",
});

const contactEmail = "contact@adjusttothegoodlifetravel.com";

const ContactPage: NextPage<any> = (props) => {
  const [submitted, setSubmitted] = useState(false);

  useUpdateEffect(() => {
    setSubmitted(false);
  }, [props]);

  if (submitted) {
    return (
      <SiteLayout>
        <MetaComponent />

        <Container className={successContainer}>
          <div className="text-center w-100">
            <FontAwesomeIcon icon={faEnvelopeCircleCheck} size="6x" color="#198754" />

            <h1 className="display-4 fw-bold mt-4">Message Sent!</h1>

            <p className="lead text-muted">Thank you for reaching out.</p>

            <p className="text-muted">We'll review your request and contact you as soon as possible.</p>

            <Link href="/blogs/" className={blackLinkCss}>
              Browse our latest blog posts →
            </Link>
          </div>
        </Container>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <MetaComponent />

      <div className={pageBackground}>
        <Container className="py-5" style={{ maxWidth: 1120 }}>
          {/* HERO */}

          <div className={hero}>
            <PageHeaderComponent type="h1" className="text-center mb-3">
              Let's Create Your Next Unforgettable Journey
            </PageHeaderComponent>

            <p className="text-center text-muted mb-5">
              <b>Every extraordinary vacation begins with understanding what inspires you.</b> Whether you're dreaming of sailing on an{" "}
              <b>ocean, river, luxury, or expedition cruise</b>, relaxing at an exceptional all-inclusive resort, exploring Europe on a custom-designed itinerary, or embarking on
              the adventure of a lifetime, we're here to bring your vision to life. Share a few details below, and we'll create a personalized travel experience designed
              exclusively for you.
            </p>
          </div>

          {/* CONTACT CARDS */}

          <Row className="g-4 mb-4">
            <Col md={4}>
              <Card className={`${card} h-100 text-center`}>
                <Card.Body className="py-4">
                  <div className={iconCircle}>
                    <FontAwesomeIcon icon={faPhone} size="lg" />
                  </div>

                  <h4 className="fw-bold mb-2">Call Us</h4>

                  <a href="tel:+19198103743" className={contactLink}>
                    (919) 810-3743
                  </a>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className={`${card} h-100 text-center`}>
                <Card.Body className="py-4">
                  <div className={iconCircle}>
                    <FontAwesomeIcon icon={faEnvelope} size="lg" />
                  </div>

                  <h4 className="fw-bold mb-2">Email Us</h4>

                  <a href={`mailto:${contactEmail}`} className={contactLink}>
                    {contactEmail}
                  </a>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className={`${card} h-100 text-center`}>
                <Card.Body className="py-4">
                  <div className={iconCircle}>
                    <FontAwesomeIcon icon={faMessage} size="lg" />
                  </div>

                  <h4 className="fw-bold mb-2">Send us a Message</h4>

                  {/* <div className={muted}>We will get back to you at our earliest convenience.</div> */}
                  <div className={muted}>We look forward to connecting with you and will respond shortly.</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* FORM */}

          <AlertProvider>
            <AlertComponent />

            <FormComponent setSubmitted={setSubmitted} />
          </AlertProvider>
        </Container>
      </div>
    </SiteLayout>
  );
};

export default ContactPage;
